import type { FilterOptionsState } from '@mui/material';
import Autocomplete, {
  createFilterOptions,
  type AutocompleteChangeReason,
  type AutocompleteInputChangeReason,
  type AutocompleteProps,
} from '@mui/material/Autocomplete';
import MenuItem from '@mui/material/MenuItem';
import TextField, { type TextFieldProps } from '@mui/material/TextField';
import debounce from 'lodash/debounce';
import { forwardRef, useCallback, useState } from 'react';

import { Flex } from '../../flex';
import { CircularProgress } from '../../progress';
import { Text } from '../../text';
import type { ItemComponentProps, OptionSelectionProps } from '../types';

interface SelectionOption extends OptionSelectionProps {
  inputValue?: string;
}

type SearchSelectionProps = Omit<
  AutocompleteProps<OptionSelectionProps, false, boolean, false>,
  'renderInput' | 'renderOption'
> &
  Pick<
    TextFieldProps,
    | 'label'
    | 'error'
    | 'helperText'
    | 'required'
    | 'variant'
    | 'color'
    | 'inputRef'
    | 'slotProps'
    | 'InputProps'
    | 'placeholder'
  > & {
    itemComponent?: React.FC<ItemComponentProps>;
    mode?: 'normal' | 'fetch-debounce';
    debounceTime?: number;
    addable?: boolean;
    onSearch?: (inputValue: string | null) => void;
  };

function DefaultItem(props: Readonly<ItemComponentProps>) {
  const { option } = props;

  return <Text>{option.label}</Text>;
}

const SearchSelectionFilter = createFilterOptions<SelectionOption>();

const SearchSelection = forwardRef((props: SearchSelectionProps, ref) => {
  const {
    placeholder,
    helperText,
    error: hasError,
    label,
    options,
    required: isRequired,
    variant,
    color,
    inputRef,
    loading: isLoading,
    itemComponent: ItemComponent = DefaultItem,
    mode = 'normal',
    debounceTime = 700,
    addable: isAddable = false,
    onSearch,
    slotProps,
    ...restProps
  } = props;

  const [isSearching, setIsSearching] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  // NOTE: There's a small behavior that you need to know
  // If you open/inputValue change or anything that trigger debouncedSearch at the same time
  // The debounce function still execute separately (not broken, just make an unnecessary fetch)
  const debouncedSearch = useCallback(
    debounce((newInputValue) => {
      onSearch?.(newInputValue);
      setIsSearching(false);
    }, debounceTime),
    [],
  );

  const handleInputValue = () => {
    const value = props.value as SelectionOption;
    if (value?.inputValue && value.label === props.inputValue) {
      return value.inputValue;
    } else {
      return props.inputValue;
    }
  };

  const handleOpen = () => {
    setIsSearching(true);
    setIsOpen(true);
    debouncedSearch(props.inputValue);
  };

  const handleValueChange = (
    event: React.SyntheticEvent,
    newValue: SelectionOption | null,
    reason: AutocompleteChangeReason,
  ) => {
    if (reason === 'selectOption') {
      props.onChange?.(event, newValue, reason);
    } else if (reason === 'clear' && event.type === 'click') {
      props.onChange?.(event, props.defaultValue ?? null, reason);
      if (isOpen) {
        setIsSearching(true);
        debouncedSearch(props.defaultValue?.label ?? null);
      }
    }
  };

  const handleInputValueChange = (
    event: React.SyntheticEvent,
    newInputValue: string,
    reason: AutocompleteInputChangeReason,
  ) => {
    if (reason === 'input') {
      setIsSearching(true);
      props.onInputChange?.(event, newInputValue, reason);
      debouncedSearch(newInputValue);
    }
  };

  const handleBlur = (event: React.SyntheticEvent) => {
    const value = props.value as SelectionOption;
    setIsOpen(false);
    setIsSearching(false);
    props.onInputChange?.(event, value?.inputValue ?? value?.label ?? '', 'blur');
  };

  const handleFilterOptions = (
    options: SelectionOption[],
    params: FilterOptionsState<SelectionOption>,
  ) => {
    if (isAddable && !isSearching && !isLoading) {
      const filtered = SearchSelectionFilter(options, params);
      const { inputValue } = params;
      const trimmedInputValue = inputValue.trim();
      const isExisting = options.some((option) => trimmedInputValue === option.label);
      if (trimmedInputValue !== '' && !isExisting) {
        filtered.push({
          label: `Add "${trimmedInputValue}"`,
          inputValue: trimmedInputValue,
          value: trimmedInputValue,
        });
      }
      return filtered;
    } else {
      return options;
    }
  };

  const handleGetOptionLabel = (option: SelectionOption) => {
    if (option.inputValue) {
      return option.inputValue;
    } else {
      return option.label;
    }
  };

  const isUserTyping = mode === 'fetch-debounce' && isSearching;

  return (
    <Autocomplete
      ref={ref}
      options={!isLoading && !isUserTyping && options ? options : []}
      loading={isLoading || isUserTyping}
      loadingText='Loading...'
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          error={hasError}
          helperText={helperText}
          required={isRequired}
          variant={variant}
          color={color}
          inputRef={inputRef}
          slotProps={{
            input: {
              ...params.InputProps,
              ...slotProps?.input,
              endAdornment: (
                <>
                  <Flex
                    sx={{
                      width: 25,
                      height: 25,
                      pointerEvents: 'none',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {/*
                      NOTE: Needs to wrap this content in Flex for now to prevent an issue
                      ISSUE: If clicking at this area, the menu-item will close immediately after you mouse-up, which is wrong
                      [Hypothesis] the changes of area between loading state make a double-click event
                    */}
                    {isLoading ? <CircularProgress color='inherit' size={20} /> : null}
                  </Flex>
                  {params.InputProps.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
      renderOption={(props, option) => {
        const { key, ...restProps } = props;
        return (
          <MenuItem key={`${key}-${option.value}`} {...restProps} value={option.value}>
            <ItemComponent option={option} />
          </MenuItem>
        );
      }}
      {...restProps}
      inputValue={mode === 'fetch-debounce' ? handleInputValue() : props.inputValue}
      onOpen={mode === 'fetch-debounce' ? handleOpen : props.onOpen}
      onChange={mode === 'fetch-debounce' ? handleValueChange : props.onChange}
      onInputChange={mode === 'fetch-debounce' ? handleInputValueChange : props.onInputChange}
      onBlur={mode === 'fetch-debounce' ? handleBlur : props.onBlur}
      filterOptions={mode === 'fetch-debounce' ? handleFilterOptions : props.filterOptions}
      getOptionLabel={mode === 'fetch-debounce' ? handleGetOptionLabel : props.getOptionLabel}
    />
  );
});

SearchSelection.displayName = 'SearchSelection';

export type { SearchSelectionProps, SelectionOption };
export { SearchSelection };
